import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import Button from '@/components/ui/Button';
import { useFunnelStore } from '@/store/funnelStore';
import clsx from 'clsx';

import { usePersonalizedRecommendationFlow } from '@/hooks/usePersonalizedRecommendationFlow';
import { isGoogleOTP } from '@/utils/googleOTPData';
interface FunnelNavigationProps {
  nextUrl?: string;
  prevUrl?: string;
  preventNavigation?: boolean;
  nextButtonText?: string;
  prevButtonText?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  isNextDisable?: boolean;
  isNextLoading?: boolean;
  isPrevDisable?: boolean;
  isNextHidden?: boolean;
  nextButtonLeftIcon?: string;
  nextButtonClassName?: string;
  isSubmit?: boolean;
  isSubmitting?: boolean;
  useTabletCustomWidth?: boolean;
}

const FunnelNavigation = ({
  nextUrl,
  prevUrl,
  preventNavigation,
  onNext,
  onPrevious,
  isNextDisable,
  isNextLoading,
  isPrevDisable,
  isNextHidden,
  nextButtonText = 'Continue',
  prevButtonText = 'Go back',
  nextButtonLeftIcon,
  nextButtonClassName,
  isSubmit,
  isSubmitting,
  useTabletCustomWidth,
}: FunnelNavigationProps) => {
  const navigate = useNavigate();
  const { setGoingBack } = useFunnelStore();
  const pathname = window.location.pathname;
  const gbBlueskyPersonalizedRecommendationValue =
    usePersonalizedRecommendationFlow({
      pathname,
    }) || isGoogleOTP();
  const isVariantSelectionPage = pathname.includes('variant-selection');
  const containerClassName = clsx(
    isVariantSelectionPage
      ? 'flex flex-col lg:flex-col-reverse'
      : 'md:flex md:justify-between md:flex-row-reverse',
    'md:gap-24 mt-auto tablet:mt-0 rounded-bas pt-32 tablet:pt-48',
  );
  const handleNext = useCallback(() => {
    setGoingBack(false);
    if (onNext) {
      onNext();
    } else {
      if (!preventNavigation) {
        navigate(nextUrl || '');
      }
    }
  }, [navigate, nextUrl, onNext, preventNavigation, setGoingBack]);

  const handleBack = useCallback(() => {
    setGoingBack(true);
    if (prevUrl) navigate(prevUrl);
    if (onPrevious) onPrevious();
  }, [navigate, onPrevious, prevUrl, setGoingBack]);

  return (
    <>
      {(!isNextHidden || !isPrevDisable) && (
        <div
          className={`${containerClassName} ${gbBlueskyPersonalizedRecommendationValue && pathname.includes('intro') ? 'p-5 md:p-0' : ''}`}
        >
          {!isNextHidden && (
            <Button
              role="button"
              type={isSubmit ? 'submit' : 'button'}
              className={clsx(
                `cursor-pointer ${useTabletCustomWidth && 'tablet:!w-[169px]'} shadow-black-cta-shad`,
                nextButtonClassName,
              )}
              isLoading={isNextLoading}
              onClick={handleNext}
              disabled={isNextDisable}
              size="small"
              isSubmitting={isSubmitting}
            >
              {nextButtonLeftIcon && <img src={nextButtonLeftIcon} alt="icon" className="mr-8" />}
              {nextButtonText}
            </Button>
          )}

          {!isPrevDisable && (
            <Button
              type="button"
              className="cursor-pointer"
              bgcolor="bg-transparent hover:bg-transparent"
              textcolor="text-gray hover:text-[var(--color-button)]"
              size="small"
              isLoading={false}
              onClick={handleBack}
              disabled={isPrevDisable}
            >
              {prevButtonText}
            </Button>
          )}
          {isVariantSelectionPage && (
            <p className="mt-[20px] body-sm-var1 text-[#7C6D65]">
              Your doctor will help you make the right decision after a thorough review of your
              information.
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default FunnelNavigation;
